import { useEffect, useMemo, useState } from "react";
import { PageHero } from "@/components/public/Layout";
import { Reveal, SectionLabel, useSettings } from "@/components/public/shared";
import { useEnquiry } from "@/components/public/EnquiryProvider";
import { api } from "@/lib/api";
import { IMG } from "@/lib/images";
import { Star, Leaf } from "lucide-react";

export default function Menu() {
  const s = useSettings();
  const { open } = useEnquiry();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("All");
  const [jainOnly, setJainOnly] = useState(false);

  useEffect(() => {
    api.get("/menu").then(({ data }) => setItems(data)).catch(() => setItems([]));
    api.get("/menu/categories").then(({ data }) => setCats(data)).catch(() => setCats([]));
  }, []);

  const categories = useMemo(() => ["All", ...cats.map((c) => c.name)], [cats]);
  const filtered = items.filter((i) => (active === "All" || i.category === active) && (!jainOnly || (i.dietary || []).includes("Jain")));

  return (
    <div>
      <PageHero label="The Menu" title="A pure-vegetarian & Jain-Friendly culinary journey." subtitle="Chinese · Thai · Japanese · Italian · Mexican · European — crafted with fine-dining precision." image={IMG.dimsumClose} />

      <section className="bg-ivory py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button key={c} data-testid={`menu-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setActive(c)}
                  className={`rounded-full border px-4 py-2 text-[12px] font-medium uppercase tracking-wider transition-colors ${active === c ? "border-espresso bg-espresso text-ivory" : "border-subtle text-espresso/70 hover:border-gold hover:text-gold"}`}>
                  {c}
                </button>
              ))}
            </div>
            <button data-testid="menu-filter-jain-only" onClick={() => setJainOnly((v) => !v)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[12px] font-semibold uppercase tracking-wider transition-colors ${jainOnly ? "border-jain bg-jain text-white" : "border-jain/40 text-jain hover:bg-jain/5"}`}>
              <Leaf className="h-4 w-4" /> Jain-Friendly Only
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="menu-grid">
            {filtered.map((item, i) => (
              <Reveal key={item.id} delay={(i % 3) * 50}>
                <div className="group flex h-full flex-col overflow-hidden border border-subtle bg-ivory">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={item.image_url} alt={item.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                    <div className="absolute left-3 top-3 flex gap-1.5">
                      {item.is_bestseller && <span className="inline-flex items-center gap-1 rounded-full bg-gold px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-charcoal"><Star className="h-3 w-3" /> Bestseller</span>}
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-xl text-espresso">{item.name}</h3>
                      <span className="whitespace-nowrap font-display text-xl text-gold">₹{item.price}</span>
                    </div>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-inkmuted">{item.description}</p>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {(item.dietary || []).map((d) => (
                        <span key={d} className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${d === "Jain" ? "bg-jain/10 text-jain" : d === "Spicy" ? "bg-red-50 text-red-700" : "bg-cream text-espresso/70"}`}>{d}</span>
                      ))}
                      <span className="rounded-full bg-cream px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-espresso/60">{item.cuisine}</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          {filtered.length === 0 && <p className="py-16 text-center text-inkmuted">No items in this selection.</p>}

          {/* Order + Catering CTA */}
          <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="border border-subtle bg-cream p-8">
              <SectionLabel>Order Online</SectionLabel>
              <h3 className="mt-4 font-display text-2xl text-espresso">Craving something now?</h3>
              <p className="mt-2 text-sm text-inkmuted">For regular orders, order directly from your favourite delivery platform.</p>
              <div className="mt-6 flex gap-3">
                <a data-testid="menu-order-zomato" href={s.zomato_url || "#"} target="_blank" rel="noreferrer" className="flex-1 bg-[#CB202D] py-3 text-center text-[12px] font-semibold uppercase tracking-wider text-white hover:opacity-90">Zomato</a>
                <a data-testid="menu-order-swiggy" href={s.swiggy_url || "#"} target="_blank" rel="noreferrer" className="flex-1 bg-[#FC8019] py-3 text-center text-[12px] font-semibold uppercase tracking-wider text-white hover:opacity-90">Swiggy</a>
              </div>
            </div>
            <div className="border border-subtle bg-espresso p-8 text-ivory">
              <SectionLabel>Need Catering or Bulk Food?</SectionLabel>
              <h3 className="mt-4 font-display text-2xl">Planning something bigger?</h3>
              <p className="mt-2 text-sm text-ivory/70">For events or recurring food service, speak with our hospitality team.</p>
              <div className="mt-6 flex gap-3">
                <button data-testid="menu-cta-event" onClick={() => open("event")} className="flex-1 bg-gold py-3 text-[12px] font-semibold uppercase tracking-wider text-charcoal hover:bg-ivory">Event Catering</button>
                <button data-testid="menu-cta-bulk" onClick={() => open("bulk")} className="flex-1 border border-ivory/40 py-3 text-[12px] font-semibold uppercase tracking-wider text-ivory hover:border-gold hover:text-gold">Bulk Food</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
